import { useLanguageAwareRouting } from '@gofranz/common-components';
import {
  Box,
  Button,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  type Icon,
  IconArrowRight,
  IconBolt,
  IconCheck,
  IconCode,
  IconCoin,
  IconLock,
  IconPlugConnected,
  type IconProps,
  IconRoute,
  IconShield,
  IconShieldCheck,
  IconSparkles,
  IconX,
} from '@tabler/icons-react';
import 'altcha';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type TablerIcon = ForwardRefExoticComponent<IconProps & RefAttributes<Icon>>;

interface FeatureCard {
  i18nKeyTitle: string;
  i18nKeyDescription: string;
  icon: TablerIcon;
}

interface HowItWorksStep {
  numberKey: string;
  titleKey: string;
  descriptionKey: string;
  icon: TablerIcon;
}

interface ComparisonRow {
  label: string;
  formshiveKey: string;
  othersKey: string;
}

const FEATURES: FeatureCard[] = [
  {
    i18nKeyTitle: 'welcome.features.instant.title',
    i18nKeyDescription: 'welcome.features.instant.description',
    icon: IconBolt,
  },
  {
    i18nKeyTitle: 'welcome.features.developer.title',
    i18nKeyDescription: 'welcome.features.developer.description',
    icon: IconCode,
  },
  {
    i18nKeyTitle: 'welcome.features.security.title',
    i18nKeyDescription: 'welcome.features.security.description',
    icon: IconShield,
  },
  {
    i18nKeyTitle: 'welcome.features.pricing.title',
    i18nKeyDescription: 'welcome.features.pricing.description',
    icon: IconCoin,
  },
  {
    i18nKeyTitle: 'welcome.features.freedom.title',
    i18nKeyDescription: 'welcome.features.freedom.description',
    icon: IconLock,
  },
  {
    i18nKeyTitle: 'welcome.features.features.title',
    i18nKeyDescription: 'welcome.features.features.description',
    icon: IconSparkles,
  },
];

const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    numberKey: '1',
    titleKey: 'welcome.howItWorks.step1Title',
    descriptionKey: 'welcome.howItWorks.step1Description',
    icon: IconPlugConnected,
  },
  {
    numberKey: '2',
    titleKey: 'welcome.howItWorks.step2Title',
    descriptionKey: 'welcome.howItWorks.step2Description',
    icon: IconShieldCheck,
  },
  {
    numberKey: '3',
    titleKey: 'welcome.howItWorks.step3Title',
    descriptionKey: 'welcome.howItWorks.step3Description',
    icon: IconRoute,
  },
];

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    label: 'Pricing',
    formshiveKey: 'welcome.comparison.pricing',
    othersKey: 'welcome.comparison.pricingOthers',
  },
  {
    label: 'Setup Time',
    formshiveKey: 'welcome.comparison.setup',
    othersKey: 'welcome.comparison.setupOthers',
  },
  {
    label: 'Self-Hosting',
    formshiveKey: 'welcome.comparison.selfHost',
    othersKey: 'welcome.comparison.selfHostOthers',
  },
  {
    label: 'Privacy',
    formshiveKey: 'welcome.comparison.privacy',
    othersKey: 'welcome.comparison.privacyOthers',
  },
  {
    label: 'Support',
    formshiveKey: 'welcome.comparison.support',
    othersKey: 'welcome.comparison.supportOthers',
  },
];

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
}

function SectionHeader({ title, subtitle }: SectionHeaderProps) {
  return (
    <Stack gap={6} align="center" mb={40}>
      <Title order={2} ta="center" fw={600} size="h3" style={{ letterSpacing: '-0.01em' }}>
        {title}
      </Title>
      {subtitle && (
        <Text c="dimmed" ta="center" size="sm" maw={560}>
          {subtitle}
        </Text>
      )}
    </Stack>
  );
}

interface FeatureCardItemProps {
  icon: TablerIcon;
  title: string;
  description: string;
  amberSurface: string;
  amberIcon: string;
}

function FeatureCardItem({
  icon: Icon,
  title,
  description,
  amberSurface,
  amberIcon,
}: FeatureCardItemProps) {
  return (
    <Card withBorder radius="md" p="lg" h="100%">
      <Box
        mb="md"
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: amberSurface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={amberIcon} stroke={1.75} />
      </Box>
      <Title order={3} size="h5" mb={6} fw={600}>
        {title}
      </Title>
      <Text size="sm" c="dimmed" lh={1.55}>
        {description}
      </Text>
    </Card>
  );
}

interface HeroMockupProps {
  isDark: boolean;
  codeSurface: string;
  codeText: string;
  amber: string;
  navy: string;
  cardBorder: string;
  cardSurface: string;
  postLabel: string;
  dashboardLabel: string;
}

function HeroMockup({
  isDark,
  codeSurface,
  codeText,
  amber,
  navy,
  cardBorder,
  cardSurface,
  postLabel,
  dashboardLabel,
}: HeroMockupProps) {
  const dim = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,20,25,0.55)';
  const subtle = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)';

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '4 / 3',
        borderRadius: 16,
        border: `1px solid ${cardBorder}`,
        background: cardSurface,
        padding: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 12px 32px rgba(10, 25, 47, 0.10)',
      }}
    >
      {/* Code panel */}
      <Box
        style={{
          background: codeSurface,
          borderRadius: 10,
          padding: 14,
          color: codeText,
          fontFamily: 'monospace',
          fontSize: 11,
          lineHeight: 1.6,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          overflow: 'hidden',
        }}
      >
        <Group gap={6} mb={4}>
          <Box w={8} h={8} style={{ borderRadius: '50%', background: '#ff5f57' }} />
          <Box w={8} h={8} style={{ borderRadius: '50%', background: '#febc2e' }} />
          <Box w={8} h={8} style={{ borderRadius: '50%', background: '#28c840' }} />
          <Text
            size="xs"
            ml={6}
            style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace' }}
          >
            {postLabel}
          </Text>
        </Group>
        <Text component="div" style={{ fontFamily: 'monospace', fontSize: 11, color: amber }}>
          {'<form'}
        </Text>
        <Text
          component="div"
          style={{ fontFamily: 'monospace', fontSize: 11, color: codeText, paddingLeft: 12 }}
        >
          {'action="https://'}
        </Text>
        <Text
          component="div"
          style={{ fontFamily: 'monospace', fontSize: 11, color: amber, paddingLeft: 12 }}
        >
          {'  formshive.com/f/x"'}
        </Text>
        <Text
          component="div"
          style={{ fontFamily: 'monospace', fontSize: 11, color: codeText, paddingLeft: 12 }}
        >
          {'method="POST">'}
        </Text>
        <Text
          component="div"
          style={{
            fontFamily: 'monospace',
            fontSize: 11,
            color: 'rgba(255,255,255,0.7)',
            paddingLeft: 12,
          }}
        >
          {'  <input name="email" />'}
        </Text>
        <Text
          component="div"
          style={{
            fontFamily: 'monospace',
            fontSize: 11,
            color: 'rgba(255,255,255,0.7)',
            paddingLeft: 12,
          }}
        >
          {'  <textarea …/>'}
        </Text>
        <Text component="div" style={{ fontFamily: 'monospace', fontSize: 11, color: amber }}>
          {'</form>'}
        </Text>
      </Box>

      {/* Dashboard panel */}
      <Stack gap={10} style={{ overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Text size="xs" fw={600} c={isDark ? 'white' : navy}>
            {dashboardLabel}
          </Text>
          <Box w={28} h={6} style={{ borderRadius: 3, background: amber }} />
        </Group>

        {/* Bar chart */}
        <Group align="flex-end" gap={6} style={{ height: 56 }}>
          {[35, 55, 40, 70, 50, 85, 60].map((h, i) => (
            <Box
              key={i}
              style={{
                flex: 1,
                height: `${h}%`,
                background: i % 3 === 0 ? amber : navy,
                borderRadius: 3,
                opacity: isDark ? 0.85 : 1,
              }}
            />
          ))}
        </Group>

        {/* Integration rows */}
        <Stack gap={6}>
          {['Email', 'Webhook', 'Zapier'].map((label) => (
            <Group
              key={label}
              justify="space-between"
              p={6}
              style={{
                borderRadius: 6,
                background: subtle,
              }}
            >
              <Group gap={6}>
                <Box w={14} h={14} style={{ borderRadius: 4, background: amber }} />
                <Text size="xs" fw={500} c={isDark ? 'white' : navy}>
                  {label}
                </Text>
              </Group>
              <Text size="xs" c={dim}>
                ✓
              </Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

export function Welcome() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';
  const { createLanguageURL } = useLanguageAwareRouting();

  const amber = theme.colors['brand-primary'][6];
  const navy = theme.colors['brand-secondary'][6];
  const cardBorder = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
  const cardSurface = isDark ? theme.colors.dark[6] : theme.white;
  const codeSurface = isDark ? '#050400' : '#0A192F';
  const codeText = '#dee3ea';
  const amberSurface = isDark ? 'rgba(255, 184, 0, 0.16)' : '#FFF4D6';
  const amberRowTint = isDark ? 'rgba(255, 184, 0, 0.06)' : '#FFFAEC';
  const altRowTint = isDark ? 'rgba(255, 255, 255, 0.02)' : theme.colors.gray[0];

  return (
    <Container size="xl" px="md" py={64}>
      <Stack gap={96}>
        {/* Hero */}
        <Box pt={24}>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: 56,
              alignItems: 'center',
            }}
            className="formshive-hero-grid"
          >
            <Stack justify="center" gap="md" maw={520} style={{ minWidth: 0 }}>
              <Title
                order={1}
                size="2.5rem"
                lh={1.05}
                fw={700}
                style={{ letterSpacing: '-0.02em' }}
              >
                {t('welcome.tagline')}
              </Title>
              <Text size="md" c={isDark ? theme.colors.gray[3] : theme.colors.gray[7]} lh={1.55}>
                {t('welcome.description')}
              </Text>
              <Group gap="md" mt={2} wrap="wrap">
                <Group gap={6} wrap="nowrap">
                  <IconBolt size={14} color={amber} stroke={2.25} />
                  <Text size="xs" fw={500} c="dimmed">
                    {t('welcome.heroPills.setup')}
                  </Text>
                </Group>
                <Group gap={6} wrap="nowrap">
                  <IconShield size={14} color={amber} stroke={2.25} />
                  <Text size="xs" fw={500} c="dimmed">
                    {t('welcome.heroPills.privacy')}
                  </Text>
                </Group>
                <Group gap={6} wrap="nowrap">
                  <IconCode size={14} color={amber} stroke={2.25} />
                  <Text size="xs" fw={500} c="dimmed">
                    {t('welcome.heroPills.noBackend')}
                  </Text>
                </Group>
              </Group>
              <Group gap="sm" mt={8}>
                <Button
                  component={Link}
                  to={createLanguageURL('/signup')}
                  size="md"
                  radius="md"
                  rightSection={<IconArrowRight size={16} />}
                  styles={{
                    root: {
                      backgroundColor: amber,
                      color: theme.colors.dark[8],
                    },
                  }}
                >
                  {t('glob_navigation.getStarted')}
                </Button>
                <Button
                  component={Link}
                  to={createLanguageURL('/docs')}
                  size="md"
                  radius="md"
                  variant="outline"
                >
                  {t('welcome.hero.readDocs')}
                </Button>
              </Group>
            </Stack>
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HeroMockup
                isDark={isDark}
                codeSurface={codeSurface}
                codeText={codeText}
                amber={amber}
                navy={navy}
                cardBorder={cardBorder}
                cardSurface={cardSurface}
                postLabel={t('welcome.hero.mockupTitle')}
                dashboardLabel={t('welcome.hero.mockupDashboard')}
              />
            </Box>
          </Box>
        </Box>

        {/* Features */}
        <Box>
          <SectionHeader
            title={t('welcome.featuresHeader.title')}
            subtitle={t('welcome.featuresHeader.subtitle')}
          />
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl">
            {FEATURES.map((feature) => (
              <FeatureCardItem
                key={feature.i18nKeyTitle}
                icon={feature.icon}
                title={t(feature.i18nKeyTitle)}
                description={t(feature.i18nKeyDescription)}
                amberSurface={amberSurface}
                amberIcon={amber}
              />
            ))}
          </SimpleGrid>
        </Box>

        {/* How It Works */}
        <Box>
          <SectionHeader
            title={t('welcome.howItWorks.title')}
            subtitle={t('welcome.howItWorks.subtitle')}
          />
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={0}>
            {HOW_IT_WORKS_STEPS.map((step, idx) => (
              <Stack
                key={step.numberKey}
                align="center"
                gap="md"
                ta="center"
                px="lg"
                style={{
                  borderLeft: idx > 0 ? `1px solid ${cardBorder}` : 'none',
                }}
              >
                <Box
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: amberSurface,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <step.icon size={32} color={amber} stroke={1.75} />
                </Box>
                <Title order={3} size="h4" fw={600}>
                  <Text component="span" inherit c={amber}>
                    {step.numberKey}.
                  </Text>{' '}
                  {t(`welcome.howItWorks.step${step.numberKey}Title`)}
                </Title>
                <Text size="sm" c="dimmed" lh={1.6} maw={300}>
                  {t(step.descriptionKey)}
                </Text>
              </Stack>
            ))}
          </SimpleGrid>
        </Box>

        {/* Comparison */}
        <Box>
          <SectionHeader
            title={t('welcome.comparison.title')}
            subtitle={t('welcome.comparison.subtitle')}
          />
          <Card withBorder radius="md" p={0} style={{ overflow: 'hidden' }}>
            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                borderBottom: `1px solid ${cardBorder}`,
              }}
            >
              <Box
                px="lg"
                py="md"
                style={{ backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0] }}
              >
                <Text fw={600} size="sm">
                  {t('welcome.comparison.feature')}
                </Text>
              </Box>
              <Box px="lg" py="md" style={{ backgroundColor: amberRowTint, textAlign: 'center' }}>
                <Text fw={700} size="sm" c={amber}>
                  {t('welcome.comparison.formshive')}
                </Text>
              </Box>
              <Box
                px="lg"
                py="md"
                style={{
                  backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0],
                  textAlign: 'center',
                }}
              >
                <Text fw={600} size="sm" c="dimmed">
                  {t('welcome.comparison.others')}
                </Text>
              </Box>
            </Box>
            {COMPARISON_ROWS.map((row, idx) => {
              const isLast = idx === COMPARISON_ROWS.length - 1;
              const cellBorder = !isLast ? `1px solid ${cardBorder}` : 'none';
              const labelBg = idx % 2 === 1 ? altRowTint : 'transparent';
              return (
                <Box
                  key={row.label}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    alignItems: 'stretch',
                  }}
                >
                  <Box
                    px="lg"
                    py="md"
                    style={{
                      borderBottom: cellBorder,
                      backgroundColor: labelBg,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Text size="sm" fw={500}>
                      {row.label}
                    </Text>
                  </Box>
                  <Box
                    px="lg"
                    py="md"
                    style={{
                      borderBottom: cellBorder,
                      backgroundColor: amberRowTint,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Group gap={6} justify="center" wrap="nowrap">
                      <ThemeIcon size="xs" radius="xl" variant="light" color="green">
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text size="sm" fw={500} ta="center">
                        {t(row.formshiveKey)}
                      </Text>
                    </Group>
                  </Box>
                  <Box
                    px="lg"
                    py="md"
                    style={{
                      borderBottom: cellBorder,
                      backgroundColor: labelBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Group gap={6} justify="center" wrap="nowrap">
                      <ThemeIcon size="xs" radius="xl" variant="light" color="gray">
                        <IconX size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed" ta="center">
                        {t(row.othersKey)}
                      </Text>
                    </Group>
                  </Box>
                </Box>
              );
            })}
          </Card>
        </Box>
      </Stack>
    </Container>
  );
}
